﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import {
    IndividualIdentifierTypes,
    OrganizationIdentifierSubTypes,
    OrganizationIdentifierTypes,
} from "~base/Internationalization/ConfigurationInfo";
import { IdentifierParseTypes } from "~base/Types/Identifier";
import * as sessionModel from "~base/Services/API/Session/Model";
import {
    getIdentifierTypesForModels,
    getSessionModelForSubjectTypes,
    sortSessionItemsDescending,
} from "./Functions";
import { getSubjectIdentifierConType } from "~base/Types/IdentifierConverter";
import { IntlContext } from "~base/Internationalization/IntlContext";

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type SetLoading = { type: "setLoading" };
type SetSessionState = { type: "setSessionState"; state: SessionState };
type InitDataLoadedState = {
    type: "initDataLoaded";
    data: sessionModel.GetStartInfoResponse;
    subjectIdentifierFromQs: string;
    sessionModelFqnFromQs?: string | null;
    subjectIdentifierTypes: IdentifierParseTypes;
    requireSessionModelSelection?: boolean;
};
type SessionsForSubjectLoaded = {
    type: "sessionsForSubjectLoaded";
    subjectidentifier: string;
    data: sessionModel.SessionsResponse;
};
type setSubjectIdentifier = {
    type: "setSubjectIdentifier";
    value: string;
    parsedValue: IdentifierParseTypes;
};
type SetFetchError = { type: "fetchError"; message: string };
type SelectSessionModel = {
    type: "selectSessionModel";
    sessionModel: sessionModel.GetStartInfoSessionModel;
};
type SelectSessionModelLanguage = {
    type: "selectSessionModelLanguage";
    id: string;
};
type SelectOngoingSession = { type: "selectOngoingSession"; id: number };
type SelectCompletedSession = { type: "selectCompletedSession"; id: number };
type CreateNewSession = { type: "createNewSession" };
type Navigate = {
    type: "navigate";
    uri: string;
    replace?: boolean;
    forceLoadAsExternal?: boolean;
};
type PageRefresh = { type: "pageRefresh" };
type SetAcceptMessage = {
    type: "setAcceptMessage";
    titleTextCode: string;
    messageTextCode: string;
};
type ClearAcceptMessage = { type: "clearAcceptMessage"; suppressed?: boolean };
type SelectRådgivarKod = { type: "selectRådgivarKod"; kod: string };
type SetHasLoadedTextCodes = { type: "setHasLoadedTextCodes"; value: boolean };

export type StartPageViewAction =
    | SetLoading
    | SetSessionState
    | InitDataLoadedState
    | SetFetchError
    | setSubjectIdentifier
    | SelectSessionModel
    | SelectSessionModelLanguage
    | SessionsForSubjectLoaded
    | SelectOngoingSession
    | SelectCompletedSession
    | CreateNewSession
    | Navigate
    | PageRefresh
    | SetAcceptMessage
    | ClearAcceptMessage
    | SelectRådgivarKod
    | SetHasLoadedTextCodes;

export interface StartPageState {
    startInfo?: sessionModel.GetStartInfoResponse;
    subjectSessions?: Array<sessionModel.Session>;
    loadedSessionsForSubjectIdentifier?: string;
    selectedSessionModelLanguage?: string;
    selectedSessionModelId?: number;
    selectedOngoingSessionId?: number;
    selectedCompletedSessionId?: number;
    subjectIdentifier: string;
    subjectIdentifierType: string;
    subjectIdComesFromQS?: boolean;
    matchingSubjectTypes: Array<
        IndividualIdentifierTypes | OrganizationIdentifierTypes
    >;
    selectedSessionState: SessionState;
    errorMessage?: string;
    /** A message that will be displayed and accepted by the user before creating/resuming a session. */
    acceptMessageBodyTextCode?: string;
    /** Title of the message that will be displayed and accepted by the user before creating/resuming a session. */
    acceptMessageTitleTextCode?: string;
    acceptMessageSuppressed?: boolean;
    loading: boolean;
    navigateUri?: string;
    navigateReplace?: boolean;
    navigateForceExternal?: boolean;
    selectedRådgivarKod?: string;
    availableRådgivarKoder?: string[];
    salesOpportunity?: string;
    hasLoadedTextCodes?: boolean;
    enableLanguageSelect?: boolean;
    requireSessionModelSelection?: boolean;
}

export interface ReducerContext {
    intl: IntlContext;
}
export type SessionState = "New" | "Ongoing" | "Completed";

export function reducer<T>(
    state: StartPageState,
    action: StartPageViewAction,
    context: ReducerContext
): StartPageState {
    switch (action.type) {
        case "setLoading": {
            return {
                ...state,
                errorMessage: null,
                loading: true,
            };
        }
        case "setSessionState": {
            return {
                ...state,
                selectedSessionState: action.state,
                errorMessage: null,
            };
        }
        case "initDataLoaded": {
            const subjectId = action.data.RestrictToSubject
                ? action.data.RestrictedSubjectIdentifier
                : action.subjectIdentifierFromQs;
            const matchingSubjectTypes = action.data.RestrictToSubject
                ? getIdentifierTypesForModels([
                      action.data.RestrictedSubjectIdentifierType,
                  ]).flat()
                : action.subjectIdentifierTypes?.matchingTypes;

            // A session model named in the query string is preselected, but only when it is one the
            // user may actually start for this subject type. A valid one also takes precedence over
            // requireSessionModelSelection, in every instance. Anything else is ignored, so the flow is
            // picked the usual way rather than the select showing a value that is not an option.
            const sessionModelFromQs = !action.sessionModelFqnFromQs
                ? undefined
                : getSessionModelForSubjectTypes(
                      matchingSubjectTypes ?? [],
                      action.data.SessionModels
                  ).find(
                      (x) => x.SessionModelFqn === action.sessionModelFqnFromQs
                  );

            const sessionModel =
                sessionModelFromQs ??
                (action.requireSessionModelSelection
                    ? undefined
                    : action.data.SessionModels?.find((_) => true));

            // The start page's identifier types are mutually exclusive, so there is at most one match.
            // Same as setSubjectIdentifier, so a prefilled identifier behaves like a typed one.
            const subjectIdentifierType = action.data.RestrictToSubject
                ? action.data.RestrictedSubjectIdentifierType
                : getSubjectIdentifierConType(matchingSubjectTypes?.[0]);
            const availableRådgivarKoder = action.data.RådgivarKoder ?? [];
            const selectedRådgivarKod =
                action.data.RådgivarKodDefault ?? availableRådgivarKoder[0];

            return {
                ...state,
                startInfo: action.data,
                loading: false,
                selectedSessionModelId: sessionModel?.SessionModelId,
                selectedSessionModelLanguage: sessionModel?.Locales?.find(
                    (_) => true
                )?.Id,
                subjectIdentifier: subjectId ?? state.subjectIdentifier,
                subjectIdentifierType: subjectIdentifierType,
                subjectIdComesFromQS: subjectId ? true : false,
                matchingSubjectTypes:
                    matchingSubjectTypes ?? state.matchingSubjectTypes,
                selectedRådgivarKod: selectedRådgivarKod,
                availableRådgivarKoder: availableRådgivarKoder,
                errorMessage: context.intl.tryFormatText(
                    sessionModel?.SessionModelRestrictedWithMessage
                ),
                salesOpportunity: action.data.SalesOpportunity,
                requireSessionModelSelection:
                    action.requireSessionModelSelection,
            };
        }
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "setSubjectIdentifier": {
            const matchingSubTypes = action.parsedValue.matchingTypes;
            const subjectIdType = getSubjectIdentifierConType(
                matchingSubTypes[0]
            );
            const sessionModels = getSessionModelForSubjectTypes(
                matchingSubTypes,
                state.startInfo?.SessionModels
            );
            const sessionModel = state.requireSessionModelSelection
                ? undefined
                : sessionModels?.find((_) => true);
            const sessionModelLanguage = sessionModel?.Locales?.find(
                (_) => true
            )?.Id;

            return {
                ...state,
                subjectIdentifier: action.value,
                subjectIdentifierType: subjectIdType,
                subjectIdComesFromQS: false,
                matchingSubjectTypes: matchingSubTypes,
                selectedSessionModelId: sessionModel?.SessionModelId,
                selectedSessionModelLanguage: sessionModelLanguage,
                errorMessage: null,
            };
        }
        case "selectSessionModel": {
            return {
                ...state,
                selectedSessionModelId: action.sessionModel.SessionModelId,
                selectedSessionModelLanguage:
                    action.sessionModel.Locales.find(
                        (x) => x.Id == state.selectedSessionModelLanguage
                    )?.Id ?? action.sessionModel.Locales?.find((_) => true)?.Id,
                errorMessage: context.intl.tryFormatText(
                    action.sessionModel.SessionModelRestrictedWithMessage
                ),
            };
        }
        case "selectSessionModelLanguage": {
            return {
                ...state,
                selectedSessionModelLanguage: action.id,
                errorMessage: null,
            };
        }
        case "sessionsForSubjectLoaded": {
            return {
                ...state,
                loadedSessionsForSubjectIdentifier: action.subjectidentifier,
                subjectSessions: action.data.items,
                selectedOngoingSessionId: action.data.items
                    .sort(sortSessionItemsDescending)
                    .find((x) => x.state == "Active")?.id,
                selectedCompletedSessionId: action.data.items
                    .sort(sortSessionItemsDescending)
                    .find((x) => x.state == "Completed")?.id,
                errorMessage: null,
            };
        }
        case "selectOngoingSession": {
            return {
                ...state,
                selectedOngoingSessionId: action.id,
                errorMessage: null,
            };
        }
        case "selectCompletedSession": {
            return {
                ...state,
                selectedCompletedSessionId: action.id,
                errorMessage: null,
            };
        }
        case "navigate": {
            return {
                ...state,
                errorMessage: null,
                loading: action.uri ? true : false,
                navigateUri: action.uri,
                navigateReplace: action.replace,
                navigateForceExternal: action.forceLoadAsExternal,
            };
        }
        case "pageRefresh": {
            if (state.navigateUri) {
                return { ...state, loading: false, navigateUri: null };
            }
            return state;
        }
        case "setAcceptMessage": {
            return {
                ...state,
                acceptMessageTitleTextCode: action.titleTextCode,
                acceptMessageBodyTextCode: action.messageTextCode,
            };
        }
        case "clearAcceptMessage": {
            return {
                ...state,
                acceptMessageSuppressed:
                    action.suppressed == null
                        ? state.acceptMessageSuppressed
                        : action.suppressed,
                acceptMessageBodyTextCode: null,
                acceptMessageTitleTextCode: null,
            };
        }
        case "selectRådgivarKod": {
            return {
                ...state,
                selectedRådgivarKod: action.kod,
            };
        }

        case "setHasLoadedTextCodes": {
            return {
                ...state,
                hasLoadedTextCodes: action.value,
            };
        }
    }
}
